import { getStoreInfo } from './api';
import i18n from '../i18n';

/**
 * v5 — Mode Rush : blocage temporaire des commandes.
 *
 * Le restaurateur peut mettre le kiosk en pause depuis la console.
 * Le kiosk lit le champ "kiosk" de la réponse (le site web lit "web").
 *
 * Endpoint : POST /v3/api/store/get_rush_status
 * headers : storeid, token, type:0, lang
 * body : {}
 */

const API_BASE = 'https://api.restomi.fr/v3/api/store';

/** Statut d'un canal (kiosk ou web) */
export interface RushChannelStatus {
  active: boolean;
  remaining_seconds: number;
  message: string;
  /** v5 fermeture programmée (congés) : true = pas de compte à rebours, message seul.
   *  Optionnel : si absent, on retombe sur le comportement rush classique (compteur). */
  scheduled?: boolean;
  /** Fin du blocage (congés). Peut être un timestamp epoch (s ou ms) ou une string ISO. */
  until?: number | string;
  /** Début du blocage. Même format que until. */
  from?: number | string;
}

export interface RushStatusResponse {
  success: boolean;
  web?: RushChannelStatus;
  kiosk?: RushChannelStatus;
}

/** Statut inactif — utilisé comme fallback sûr (ne jamais bloquer sur erreur réseau) */
export const RUSH_INACTIVE: RushChannelStatus = {
  active: false,
  remaining_seconds: 0,
  message: '',
};

function buildHeaders(): HeadersInit | null {
  const storeInfo = getStoreInfo();
  if (!storeInfo) return null;
  return {
    'Content-Type': 'application/json',
    storeid: storeInfo.store_id,
    token: storeInfo.server_token,
    type: '0',
    lang: i18n.language || 'fr',
  };
}

/**
 * Récupère le statut rush pour le canal KIOSK.
 *
 * En cas d'erreur (réseau, store info absente, réponse invalide) → retourne null.
 * L'appelant doit traiter null comme "pas de blocage" : on ne bloque JAMAIS
 * les commandes à cause d'un problème technique côté kiosk.
 */
export async function fetchRushStatus(): Promise<RushChannelStatus | null> {
  try {
    const headers = buildHeaders();
    if (!headers) {
      console.warn('[Rush] Store info non disponible');
      return null;
    }
    const response = await fetch(`${API_BASE}/get_rush_status`, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      console.warn('[Rush] get_rush_status HTTP', response.status);
      return null;
    }
    const data: RushStatusResponse = await response.json();
    if (!data.success) {
      console.warn('[Rush] get_rush_status success=false', data);
      return null;
    }
    // Le KIOSK lit le champ "kiosk" (le site web lirait "web")
    const kiosk = data.kiosk;
    if (!kiosk || typeof kiosk.active !== 'boolean') {
      console.warn('[Rush] Champ "kiosk" absent ou invalide', data);
      return null;
    }
    const status: RushChannelStatus = {
      active: kiosk.active,
      remaining_seconds: Math.max(0, Number(kiosk.remaining_seconds) || 0),
      message: kiosk.message || '',
      // v5 fermeture programmée : champs optionnels, propagés seulement s'ils existent
      scheduled: kiosk.scheduled === true,
      until: kiosk.until,
      from: kiosk.from,
    };
    console.log('[Rush] get_rush_status ←', status);
    return status;
  } catch (e) {
    console.error('[Rush] fetchRushStatus error', e);
    return null;
  }
}

/**
 * Formate un nombre de secondes en compte à rebours lisible.
 *  - < 1h  → "12:35" (mm:ss)
 *  - >= 1h → "1h 05min"
 */
/**
 * Faut-il afficher un compte à rebours, ou juste le message ?
 *
 * Un rush court → compteur ("réouverture dans 12:35").
 * Une fermeture programmée (congés) → message seul ("Fermé, retour lundi"),
 * car un décompte de plusieurs jours en secondes n'a aucun sens.
 *
 * Défensif — vrai si l'UNE de ces conditions est remplie :
 *  - scheduled === true (le backend le dit explicitement)
 *  - remaining_seconds indique plus de 24h (une fermeture, pas un rush)
 *  - remaining_seconds absent/0 alors que le blocage est actif (pas de fin chiffrée)
 *
 * Si scheduled est absent et la durée est courte → on garde le compteur (comportement
 * rush actuel, aucune régression).
 */
const ONE_DAY_SECONDS = 24 * 60 * 60;

export function isScheduledClosure(status: RushChannelStatus): boolean {
  if (!status.active) return false;
  if (status.scheduled === true) return true;
  if (status.remaining_seconds > ONE_DAY_SECONDS) return true;
  if (!status.remaining_seconds || status.remaining_seconds <= 0) return true;
  return false;
}

/**
 * Parse le champ `until` en Date, de façon DÉFENSIVE.
 *
 * Le format backend n'étant pas garanti, on gère automatiquement :
 *  - epoch en SECONDES  (ex: 1752000000)     → détecté si < 10^12
 *  - epoch en MILLISECONDES (ex: 1752000000000)
 *  - string ISO 8601    (ex: "2026-07-21T09:00:00Z")
 *
 * Garde-fous : on rejette (retourne null) toute date invalide, dans le passé,
 * ou à plus de 60 jours (valeur aberrante). Mieux vaut ne rien afficher qu'une
 * date fausse sur un écran bloquant.
 */
const MAX_REASONABLE_DAYS = 60;

export function parseUntilDate(until: number | string | undefined): Date | null {
  if (until === undefined || until === null || until === '') return null;

  let date: Date | null = null;

  if (typeof until === 'number') {
    // Heuristique s vs ms : un epoch en secondes est < 10^12 (~ an 33658 en ms).
    const ms = until < 1e12 ? until * 1000 : until;
    date = new Date(ms);
  } else if (typeof until === 'string') {
    const asNum = Number(until);
    if (!Number.isNaN(asNum) && until.trim() !== '') {
      // string purement numérique → même logique epoch
      const ms = asNum < 1e12 ? asNum * 1000 : asNum;
      date = new Date(ms);
    } else {
      // string ISO
      date = new Date(until);
    }
  }

  if (!date || Number.isNaN(date.getTime())) return null;

  // Garde-fous de vraisemblance
  const now = Date.now();
  const diffMs = date.getTime() - now;
  if (diffMs < -60_000) return null; // déjà passé (tolérance 1 min)
  if (diffMs > MAX_REASONABLE_DAYS * 24 * 3600 * 1000) return null; // aberrant

  return date;
}

/**
 * Formate la date de réouverture pour affichage client, dans la langue courante.
 * Ex (fr) : "lundi 21 juillet à 09:00".
 * Retourne null si la date est inexploitable (→ on n'affiche rien).
 */
export function formatReopenDate(
  until: number | string | undefined,
  locale: string = 'fr'
): string | null {
  const date = parseUntilDate(until);
  if (!date) return null;
  try {
    const dayPart = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);
    // Heure seulement si elle n'est pas minuit pile (souvent = date sans heure précise)
    const h = date.getHours();
    const m = date.getMinutes();
    if (h === 0 && m === 0) return dayPart;
    const timePart = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
    return `${dayPart} · ${timePart}`;
  } catch {
    return null;
  }
}

export function formatRushCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}min`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Détecte une réponse create_order refusée pour cause de rush.
 * Le backend renvoie : { success: false, reason: "rush_active", remaining_seconds, error_description }
 */
export function parseRushRejection(response: any): RushChannelStatus | null {
  if (!response || response.success !== false) return null;
  if (response.reason !== 'rush_active') return null;
  return {
    active: true,
    remaining_seconds: Math.max(0, Number(response.remaining_seconds) || 0),
    message: response.error_description || '',
  };
}
